import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/common";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          <div className="mb-8"><BrandLogo className="max-h-16 max-w-[220px]" testid="login-logo" /></div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Nini's Kitchen · Niro · Baked By Nini's</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">Feedback Hub</h1>
          <p className="mt-3 text-base text-slate-500">Every review, rating and complaint from every outlet — in one place.</p>
          <div className="mt-10 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl text-base" placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl text-base" data-testid="login-password-input" />
            </div>
            {error && <div data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
            <Button type="submit" disabled={busy} className="h-12 w-full rounded-xl text-base font-semibold" data-testid="login-submit-button">
              {busy ? <Spinner /> : "Sign in"}
            </Button>
            <p className="text-center text-sm text-slate-400">Don't have an account? Ask your admin to add you.</p>
          </div>
        </form>
      </div>
      <div className="relative hidden lg:block">
        <img src="/login.jpg" alt="Restaurant interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-display text-2xl font-semibold">Listen. Fix. Delight.</div>
          <div className="mt-1 text-sm text-white/80">Reelo · Zomato · Swiggy · Google · Calls & messages</div>
        </div>
      </div>
    </div>
  );
}
